import React, { useState, useEffect } from 'react';
import { string, object } from 'prop-types';
import { Flex } from 'rebass';
import { SortAscending, SortDescending, Filter } from '@nike/nike-design-system-icons';

import { sortHighlightColor, filterHightlightColor } from '../tableServerSide/constants';

import ColumnFilterSortMenu from '../tableServerSide/ColumnFilterSortMenu';

const ColumnFilterSort = ({ align, header, searchHelperText, column }) => {
  const [isEllipsisIconVisible, setIsEllipsisIconVisible] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [filterValue, setFilterValue] = useState('');

  const {
    setFilter,
    filterValue: colFilterValue,
    isSorted,
    isSortedDesc,
    clearSortBy,
    toggleSortBy,
    disableFilters,
    disableSortBy
  } = column;

  const isSortedAsc = isSorted && !isSortedDesc;
  const isFiltering = !!colFilterValue;
  const isFilteringOrSorting = isFiltering || isSorted;

  // Set Column filter as the user types
  const onFilterChange = e => {
    setFilterValue(e.target.value);
  };

  const closeIconAndMenu = () => {
    setIsEllipsisIconVisible(false);
    setIsMenuOpen(false);
  };

  // Set Column filter when user hits enter
  const onKeyDown = e => {
    if (e.keyCode === 13) {
      setFilter(filterValue);
      closeIconAndMenu();
    }
  };

  /* Clears text input if column filter is empty */
  useEffect(() => {
    if (!colFilterValue) {
      setFilterValue('');
    }
  }, [colFilterValue]);

  useEffect(() => {
    if (!isFilteringOrSorting) {
      closeIconAndMenu();
    }
  }, [isFilteringOrSorting]);

  const handleClearColumnFilter = () => {
    setFilter('');
  };

  const toggleSortAsc = () => {
    if (isSortedAsc) {
      clearSortBy();
      return;
    }
    toggleSortBy(false, true);

    closeIconAndMenu();
  };

  const toggleSortDesc = () => {
    if (isSortedDesc) {
      clearSortBy();
      return;
    }
    toggleSortBy(true, true);

    // Hide menu
    setIsEllipsisIconVisible(false);
    setIsMenuOpen(false);
  };

  const onMouseEnterColumnHover = () => {
    setIsEllipsisIconVisible(true);
  };

  const onMouseLeaveColumnHover = () => {
    if (!isMenuOpen) {
      setIsEllipsisIconVisible(false);
    }
  };

  return (
    <Flex justifyContent='space-between' onMouseEnter={onMouseEnterColumnHover} onMouseLeave={onMouseLeaveColumnHover}>
      {header}

      <Flex>
        {isSortedAsc && (
          <SortAscending
            style={{ cursor: 'pointer', color: sortHighlightColor }}
            onClick={toggleSortAsc}
            data-testid={`${header}-columnSortAscIcon`}
          />
        )}
        {isSortedDesc && (
          <SortDescending
            style={{ cursor: 'pointer', color: sortHighlightColor }}
            onClick={toggleSortDesc}
            data-testid={`${header}-columnSortDescIcon`}
          />
        )}
        {isFiltering && (
          <Filter
            data-testid={`${header}-columnFilterIcon`}
            style={{ cursor: 'pointer', color: filterHightlightColor }}
            onClick={handleClearColumnFilter}
          />
        )}

        <ColumnFilterSortMenu
          align={align}
          isEllipsisIconVisible={isEllipsisIconVisible}
          isMenuOpen={isMenuOpen}
          setIsMenuOpen={setIsMenuOpen}
          toggleSortAsc={toggleSortAsc}
          toggleSortDesc={toggleSortDesc}
          onFilterChange={onFilterChange}
          onKeyDown={onKeyDown}
          disableSort={disableSortBy}
          disableFilter={disableFilters}
          filterValue={colFilterValue || filterValue}
          isFiltering={isFiltering}
          handleClearColumnFilter={handleClearColumnFilter}
          header={header}
          isSortAsc={isSortedAsc}
          isSortDesc={!!isSortedDesc}
          closeIconAndMenu={closeIconAndMenu}
          searchHelperText={searchHelperText}
        />
      </Flex>
    </Flex>
  );
};

ColumnFilterSort.propTypes = {
  align: string,
  header: string,
  searchHelperText: string,
  column: object
};

ColumnFilterSort.defaultProps = {
  align: 'left',
  header: '',
  searchHelperText: '',
  column: {}
};

export default ColumnFilterSort;
